import type { GuideId, TourDefinition, TourStepConfig } from "../types";

/**
 * Tour definitions for all available guides
 * Each tour defines steps with targets (CSS selectors) and i18n keys
 */

// Workflow InBasket Tour
const workflowInbasketSteps: TourStepConfig[] = [
  {
    target: '[data-tour="inbasket-list"]',
    titleKey: "tours.workflowInbasket.steps.taskList.title",
    descriptionKey: "tours.workflowInbasket.steps.taskList.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="view-mode-toggle"]',
    titleKey: "tours.workflowInbasket.steps.viewModes.title",
    descriptionKey: "tours.workflowInbasket.steps.viewModes.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="task-filters"]',
    titleKey: "tours.workflowInbasket.steps.filters.title",
    descriptionKey: "tours.workflowInbasket.steps.filters.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="complete-task-button"]',
    titleKey: "tours.workflowInbasket.steps.completeTask.title",
    descriptionKey: "tours.workflowInbasket.steps.completeTask.description",
    placement: "left",
  },
];

// Entity Management Tour
const entityManagementSteps: TourStepConfig[] = [
  {
    target: '[data-tour="sidebar-menu"]',
    titleKey: "tours.entityManagement.steps.navigation.title",
    descriptionKey: "tours.entityManagement.steps.navigation.description",
    placement: "right",
  },
  {
    target: '[data-tour="entity-list"]',
    titleKey: "tours.entityManagement.steps.listView.title",
    descriptionKey: "tours.entityManagement.steps.listView.description",
    placement: "top",
  },
  {
    target: '[data-tour="create-button"]',
    titleKey: "tours.entityManagement.steps.createNew.title",
    descriptionKey: "tours.entityManagement.steps.createNew.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="import-button"]',
    titleKey: "tours.entityManagement.steps.importData.title",
    descriptionKey: "tours.entityManagement.steps.importData.description",
    placement: "bottom",
  },
];

// Import Wizard Tour
const importWizardSteps: TourStepConfig[] = [
  {
    target: '[data-tour="import-button"]',
    titleKey: "tours.importWizard.steps.openImport.title",
    descriptionKey: "tours.importWizard.steps.openImport.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="download-template"]',
    titleKey: "tours.importWizard.steps.downloadTemplate.title",
    descriptionKey: "tours.importWizard.steps.downloadTemplate.description",
    placement: "bottom",
    waitForElement: true,
  },
  {
    target: '[data-tour="file-upload"]',
    titleKey: "tours.importWizard.steps.uploadFile.title",
    descriptionKey: "tours.importWizard.steps.uploadFile.description",
    placement: "top",
    waitForElement: true,
  },
  {
    target: '[data-tour="column-mapping"]',
    titleKey: "tours.importWizard.steps.mapColumns.title",
    descriptionKey: "tours.importWizard.steps.mapColumns.description",
    placement: "top",
    waitForElement: true,
  },
];

// Lifecycle States Tour
const lifecycleStatesSteps: TourStepConfig[] = [
  {
    target: '[data-tour="lifecycle-display"]',
    titleKey: "tours.lifecycleStates.steps.stateDisplay.title",
    descriptionKey: "tours.lifecycleStates.steps.stateDisplay.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="current-state"]',
    titleKey: "tours.lifecycleStates.steps.currentState.title",
    descriptionKey: "tours.lifecycleStates.steps.currentState.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="promote-button"]',
    titleKey: "tours.lifecycleStates.steps.promoteAction.title",
    descriptionKey: "tours.lifecycleStates.steps.promoteAction.description",
    placement: "left",
  },
];

// Column Management Tour
const columnManagementSteps: TourStepConfig[] = [
  {
    target: '[data-tour="view-mode-toggle"]',
    titleKey: "tours.columnManagement.steps.viewToggle.title",
    descriptionKey: "tours.columnManagement.steps.viewToggle.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="column-settings"]',
    titleKey: "tours.columnManagement.steps.columnSettings.title",
    descriptionKey: "tours.columnManagement.steps.columnSettings.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="column-checkboxes"]',
    titleKey: "tours.columnManagement.steps.toggleColumns.title",
    descriptionKey: "tours.columnManagement.steps.toggleColumns.description",
    placement: "left",
    waitForElement: true,
  },
  {
    target: '[data-tour="reset-columns"]',
    titleKey: "tours.columnManagement.steps.resetDefault.title",
    descriptionKey: "tours.columnManagement.steps.resetDefault.description",
    placement: "left",
    waitForElement: true,
  },
];

// Dashboard Overview Tour
const dashboardOverviewSteps: TourStepConfig[] = [
  {
    target: '[data-tour="kpi-cards"]',
    titleKey: "tours.dashboardOverview.steps.kpiCards.title",
    descriptionKey: "tours.dashboardOverview.steps.kpiCards.description",
    placement: "bottom",
  },
  {
    target: '[data-tour="charts"]',
    titleKey: "tours.dashboardOverview.steps.charts.title",
    descriptionKey: "tours.dashboardOverview.steps.charts.description",
    placement: "top",
  },
  {
    target: '[data-tour="pending-tasks"]',
    titleKey: "tours.dashboardOverview.steps.pendingTasks.title",
    descriptionKey: "tours.dashboardOverview.steps.pendingTasks.description",
    placement: "top",
  },
];

/**
 * Default entity route for tours that need to navigate to a list page.
 */
const DEFAULT_ENTITY_ROUTE = "/Lead";

/**
 * All tour definitions
 */
export const TOUR_DEFINITIONS: Record<GuideId, TourDefinition> = {
  "workflow-inbasket": {
    id: "workflow-inbasket",
    titleKey: "tours.workflowInbasket.title",
    descriptionKey: "tours.workflowInbasket.description",
    route: "/inbasket",
    priority: "high",
    icon: "sync",
    steps: workflowInbasketSteps,
  },
  "entity-management": {
    id: "entity-management",
    titleKey: "tours.entityManagement.title",
    descriptionKey: "tours.entityManagement.description",
    route: DEFAULT_ENTITY_ROUTE,
    priority: "high",
    icon: "book",
    steps: entityManagementSteps,
  },
  "import-wizard": {
    id: "import-wizard",
    titleKey: "tours.importWizard.title",
    descriptionKey: "tours.importWizard.description",
    route: DEFAULT_ENTITY_ROUTE,
    priority: "high",
    icon: "import",
    steps: importWizardSteps,
  },
  "lifecycle-states": {
    id: "lifecycle-states",
    titleKey: "tours.lifecycleStates.title",
    descriptionKey: "tours.lifecycleStates.description",
    route: DEFAULT_ENTITY_ROUTE,
    priority: "medium",
    icon: "sync",
    steps: lifecycleStatesSteps,
  },
  "column-management": {
    id: "column-management",
    titleKey: "tours.columnManagement.title",
    descriptionKey: "tours.columnManagement.description",
    route: "/inbasket",
    priority: "medium",
    icon: "table",
    steps: columnManagementSteps,
  },
  "dashboard-overview": {
    id: "dashboard-overview",
    titleKey: "tours.dashboardOverview.title",
    descriptionKey: "tours.dashboardOverview.description",
    route: "/dashboard",
    priority: "high",
    icon: "rocket",
    steps: dashboardOverviewSteps,
  },
};

/**
 * Get tour definition by ID
 */
export const getTourDefinition = (
  tourId: GuideId,
): TourDefinition | undefined => {
  return TOUR_DEFINITIONS[tourId];
};

/**
 * Get steps for a specific tour
 */
export const getTourSteps = (tourId: GuideId): TourStepConfig[] => {
  return TOUR_DEFINITIONS[tourId]?.steps ?? [];
};
